/*
 * label-maker/matrix.js — a 2D code as MODULES, sized in printer dots.
 *
 * WHY THIS EXISTS NEXT TO render.barcodeOptions()
 * ───────────────────────────────────────────────
 * Every other code on a label is drawn as a bwip-js picture fitted into its
 * box: right for a QR code on an office sheet, where "a bit bigger" is fine.
 * A DataMatrix for «Честный ЗНАК» is a different object. It goes to a thermal
 * printer whose head has a fixed grid of dots (203, 300 or 600 per inch), and
 * the scanner at the till reads it reliably only when every module is THE SAME
 * whole number of dots: a "22 × 22 mm" symbol of 36 modules at 203 dpi is
 * 4.9 dots per module, so the driver makes some modules 4 dots and some 5, and
 * the grade drops. The fix is not a better picture, it is arithmetic: the
 * module is chosen as k dots, the symbol is k × modules dots, and it is placed
 * ON the dot grid. That is a property of the geometry, so it lives here, once,
 * and BOTH backends — the SVG preview and the PDF — draw from it. Two backends
 * deciding their own size is how /label-maker's preview and print disagreed
 * three times (docs/why/primitives/print-sheets.md).
 *
 * GS1 MODE. A marking code is a GS1 DataMatrix: FNC1 (codeword 232) in the
 * FIRST position, and every separator INSIDE the data is <GS> (ASCII 29,
 * codeword 30). A second FNC1 decodes to the very same byte, so no scanner and
 * no round-trip sees it — but «Честный ЗНАК» reads the codewords and reports
 * «ошибка в структуре КМ» (/barcode-generator, 07.09.2026). `gs1Text` encodes
 * exactly that; the structure is checked at the codeword level by
 * tests/js/label-matrix.test.mjs.
 *
 * DRAWING. As filled rectangles — one per horizontal run of dark modules — on
 * whole-dot edges. A rasteriser at the printer's dpi then lands every edge on a
 * dot boundary (pixel centres sit half a dot from any edge), so there is no
 * rounding left to the driver. Vector rather than a 1-bit image at native dpi:
 * both are exact at the right dpi, the vector stays exact at any zoom of the
 * preview, is a few hundred operators, and needs no canvas.
 *
 * Pure module: bwip-js is passed in. `node --test` drives it with the vendored
 * bwip-js, the same file the page loads.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.LabelMatrix = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var MM_PER_INCH = 25.4;

    /* The module window «Честный ЗНАК» publishes for its groups (markirovka.ru,
     * «Состав кода маркировки», checked 21.10.2026): 0.255 mm is the floor for
     * every group; the ceiling is 0.615 mm for light industry, shoes, perfume,
     * tyres and cameras and 0.68 mm for most of the rest. The default window is
     * the strictest, so a label printed to it is valid for any group. */
    var MIN_MODULE_MM = 0.255;
    var MAX_MODULE_MM = 0.615;
    /* GS1 asks for one module of quiet zone; scanners at a till, through film
     * and on a curved bottle, want far more. 2 mm is what the market prints. */
    var QUIET_MM = 2;

    /* ── GS1 text for bwip-js `parsefnc` ─────────────────────────────────────
     * Input: a marking code with its group separators as real 0x1D bytes (the
     * canonical form everything upstream normalises to). A leading separator,
     * FNC1 marker or AIM prefix (]d2) is the GS1 FLAG, not data. `^` is the
     * parsefnc escape, so a literal caret in the data is written ^094. */
    function gs1Text(raw) {
        var s = String(raw == null ? '' : raw)
            .replace(/^\][A-Za-z]\d/, '')
            .replace(/\^/g, '^094')
            .replace(/^\x1d+/, '')
            .replace(/\x1d/g, '^029');
        return '^FNC1' + s;
    }

    /* Encode to a module matrix. Returns null for an empty value; throws what
     * bwip-js throws (too long, bad characters) so the caller can name it. */
    function encode(bwip, it) {
        var value = String(it && it.value != null ? it.value : '');
        if (!value) return null;
        var gs1 = !!(it.gs1 || it.symbology === 'gs1datamatrix');
        var bcid = (it.symbology === 'qrcode') ? 'qrcode' : 'datamatrix';
        /* `parse` makes ^NNN an ordinal (^029 = GS, ^094 = a literal caret);
           `parsefnc` makes ^FNC1 the function character. Both, or bwip reads
           "^0299…" as an unknown function. */
        var opts = { bcid: bcid, text: gs1 ? gs1Text(value) : value, parse: gs1, parsefnc: gs1 };
        if (bcid === 'datamatrix') opts.format = 'square';
        var raw = bwip.raw(opts);
        var r = raw && raw[0];
        if (!r || !r.pixs || !r.pixx || !r.pixy) throw new Error('matrix: encoder returned no symbol');
        var cols = r.pixx, rows = r.pixy;
        var bits = new Uint8Array(cols * rows);
        for (var i = 0; i < bits.length; i++) bits[i] = r.pixs[i] ? 1 : 0;
        return { cols: cols, rows: rows, bits: bits, gs1: gs1, bcid: bcid, size: cols + 'x' + rows };
    }

    function dotMm(dpi) { return MM_PER_INCH / dpi; }

    /* Which whole number of dots per module to print.
     *
     * Candidates are every k whose module (k dots) sits inside [minMm, maxMm].
     * Of those, the LARGEST whose symbol plus quiet zone fits the box wins —
     * bigger modules read from further and through worse print. If none fits,
     * the smallest valid k is returned with fits:false so the UI can say "the
     * label is too small for this code at this dpi" instead of shrinking the
     * module below the floor. If no k is valid at all (a 100-dpi device with a
     * 0.3 mm ceiling) the nearest is returned with the violated bound named.
     *
     * Pass `dots` to force a k (the person chose it); the verdict still says
     * whether it is inside the window and fits. */
    function pickDots(o) {
        o = o || {};
        var dpi = +o.dpi > 0 ? +o.dpi : 203;
        var d = dotMm(dpi);
        var minMm = o.minMm != null ? +o.minMm : MIN_MODULE_MM;
        var maxMm = o.maxMm != null ? +o.maxMm : MAX_MODULE_MM;
        var quiet = o.quietMm != null ? +o.quietMm : QUIET_MM;
        var cols = o.cols || 1, rows = o.rows || 1;
        var boxW = o.boxW != null ? +o.boxW : Infinity, boxH = o.boxH != null ? +o.boxH : Infinity;

        function verdict(k) {
            var m = k * d;
            var w = cols * m, h = rows * m;
            return {
                dots: k, dpi: dpi, moduleMm: m, wMm: w, hMm: h,
                quietMm: quiet,
                fits: w + 2 * quiet <= boxW + 1e-9 && h + 2 * quiet <= boxH + 1e-9,
                fitsBare: w <= boxW + 1e-9 && h <= boxH + 1e-9,
                belowMin: m < minMm - 1e-9,
                aboveMax: m > maxMm + 1e-9,
            };
        }

        if (o.dots != null && +o.dots >= 1) return verdict(Math.round(+o.dots));

        var kMin = Math.max(1, Math.ceil(minMm / d - 1e-9));
        var kMax = Math.floor(maxMm / d + 1e-9);
        if (kMax < kMin) {
            // No whole number of dots lands in the window at this dpi.
            return verdict(kMin);
        }
        var best = null;
        for (var k = kMax; k >= kMin; k--) {
            var v = verdict(k);
            if (v.fits) { best = v; break; }
        }
        return best || verdict(kMin);
    }

    /* Every whole-dot module size inside the window at this dpi — what a UI
     * offers as choices. */
    function dotChoices(dpi, minMm, maxMm) {
        var d = dotMm(+dpi > 0 ? +dpi : 203);
        var lo = minMm != null ? +minMm : MIN_MODULE_MM, hi = maxMm != null ? +maxMm : MAX_MODULE_MM;
        var out = [];
        for (var k = Math.max(1, Math.ceil(lo / d - 1e-9)); k * d <= hi + 1e-9; k++) out.push({ dots: k, moduleMm: k * d });
        return out;
    }

    /* Where the symbol goes inside its box, in LABEL millimetres, snapped so
     * that `origin + x` (page millimetres) is a whole number of dots from the
     * page edge. `origin` is the label's top-left on the page: 0,0 on a roll,
     * the cell corner on a sheet. Both backends pass the same origin for the
     * same label, so they agree to the dot. Alignment follows the item's
     * align/valign, defaulting to centre. */
    function place(it, geom, origin) {
        origin = origin || { x: 0, y: 0 };
        var b = it.box || { x: 0, y: 0, w: geom.wMm, h: geom.hMm };
        var x = b.x + (b.w - geom.wMm) / 2, y = b.y + (b.h - geom.hMm) / 2;
        var ha = it.align || 'center', va = it.valign || 'middle';
        var q = Math.min(geom.quietMm || 0, Math.max(0, (b.w - geom.wMm) / 2), Math.max(0, (b.h - geom.hMm) / 2));
        if (ha === 'left') x = b.x + q;
        else if (ha === 'right') x = b.x + b.w - geom.wMm - q;
        if (va === 'top') y = b.y + q;
        else if (va === 'bottom') y = b.y + b.h - geom.hMm - q;
        var d = dotMm(geom.dpi);
        var ax = Math.round((origin.x + x) / d) * d, ay = Math.round((origin.y + y) / d) * d;
        return { x: ax - origin.x, y: ay - origin.y, w: geom.wMm, h: geom.hMm, moduleMm: geom.moduleMm };
    }

    /* The size verdict for a laid-out item (layout.js marks it `matrix`) and
     * its encoded symbol. ONE call both backends make with the same arguments. */
    function geometry(it, enc) {
        return pickDots({
            dpi: it.dpi, dots: it.dots, minMm: it.minModuleMm, maxMm: it.maxModuleMm,
            quietMm: it.quietMm, cols: enc.cols, rows: enc.rows,
            boxW: it.box ? it.box.w : Infinity, boxH: it.box ? it.box.h : Infinity,
        });
    }

    /* Dark modules as horizontal runs: [col, row, length] in module units.
     * One rectangle per run keeps a 36 × 36 symbol near 300 operators. */
    function runs(enc) {
        var out = [];
        for (var y = 0; y < enc.rows; y++) {
            var x = 0;
            while (x < enc.cols) {
                if (!enc.bits[y * enc.cols + x]) { x++; continue; }
                var s = x;
                while (x < enc.cols && enc.bits[y * enc.cols + x]) x++;
                out.push([s, y, x - s]);
            }
        }
        return out;
    }

    /* The same runs as one SVG path in label millimetres — what the preview
     * draws. */
    function svgPath(enc, pl) {
        var m = pl.moduleMm, parts = [];
        var rr = runs(enc);
        for (var i = 0; i < rr.length; i++) {
            var r = rr[i];
            parts.push('M' + r4(pl.x + r[0] * m) + ' ' + r4(pl.y + r[1] * m) + 'h' + r4(r[2] * m) + 'v' + r4(m) + 'h' + r4(-r[2] * m) + 'z');
        }
        return parts.join('');
    }

    /* Rectangles in label millimetres [x, y, w, h] — what the PDF draws. */
    function rects(enc, pl) {
        var m = pl.moduleMm, rr = runs(enc), out = new Array(rr.length);
        for (var i = 0; i < rr.length; i++) out[i] = [pl.x + rr[i][0] * m, pl.y + rr[i][1] * m, rr[i][2] * m, m];
        return out;
    }

    function r4(v) { return Math.round(v * 10000) / 10000; }

    /* Cache key: everything that changes the MODULES (not the size). */
    function key(it) {
        return (it.gs1 || it.symbology === 'gs1datamatrix' ? 'gs1|' : 'dm|') + (it.symbology === 'qrcode' ? 'qr|' : '') + String(it.value);
    }

    return {
        MIN_MODULE_MM: MIN_MODULE_MM,
        MAX_MODULE_MM: MAX_MODULE_MM,
        QUIET_MM: QUIET_MM,
        gs1Text: gs1Text,
        encode: encode,
        dotMm: dotMm,
        pickDots: pickDots,
        dotChoices: dotChoices,
        geometry: geometry,
        place: place,
        runs: runs,
        svgPath: svgPath,
        rects: rects,
        key: key,
    };
});
